<template>
  <form class="verification-code" :class="{ success: isValid && codeValue.length === 6, error: hasError }">
    <div class="form-group">
      <label>Enter verification code <span>*</span></label>
    </div>
    <div class="input-row">
      <input
        ref="codeInput"
        type="text"
        inputmode="numeric"
        pattern="\d*"
        maxlength="6"
        v-model="codeValue"
        @input="handleInput"
        @keypress="isNumber"
        @paste="onPaste"
        @blur="validateCode"
        placeholder="Enter 6-digit code"
        autofocus
        class="verification-input"
      />
      <button type="button" @click="$emit('resend')" :disabled="countdown > 0" class="resend-btn">
        <span v-if="countdown === 0">Resend</span>
        <span v-else>{{ countdown }}s</span>
      </button>
    </div>
    <p class="error-msg" v-if="error_msg" v-html="error_msg"></p>
  </form>
</template>

<script>
export default {
  name: 'verification-code',

  data() {
    return {
      codeValue: '',
      keys_allowed: ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'],
    };
  },

  computed: {
    isValid() {
      return this.codeValue.length === 6 && /^\d{6}$/.test(this.codeValue);
    },

    hasError() {
      return this.error_msg;
    },
  },

  props: {
    error_msg: {
      type: String,
      default: '',
    },
    countdown: {
      type: Number,
      default: 0,
    },
  },

  watch: {
    codeValue(newValue) {
      // 发送更新事件，保持与原组件的兼容性
      const codeArray = newValue.split('');
      while (codeArray.length < 6) {
        codeArray.push('');
      }
      this.$emit('update', codeArray.slice(0, 6));

      // 如果输入完整，自动验证
      if (newValue.length === 6) {
        this.validateCode();
      }
    },
  },

  methods: {
    handleInput(event) {
      let value = event.target.value;

      // 只保留数字
      value = value.replace(/\D/g, '');

      // 限制最大长度为6
      if (value.length > 6) {
        value = value.slice(0, 6);
      }

      this.codeValue = value;
    },

    isNumber(event) {
      // 允许数字键
      if (!this.keys_allowed.includes(event.key)) {
        event.preventDefault();
      }
    },

    onPaste(event) {
      event.preventDefault();

      const pastedData = event.clipboardData?.getData('text').trim().replace(/\D/g, '') || ''; // 只保留数字

      if (pastedData) {
        // 验证粘贴的数据是否为数字
        const validPaste = pastedData.split('').every((char) => this.keys_allowed.includes(char));

        if (validPaste) {
          this.codeValue = pastedData.slice(0, 6); // 限制为6位
        }
      }
    },

    validateCode() {
      if (this.codeValue.length === 6) {
        if (this.isValid) {
          this.$emit('complete', this.codeValue);
        } else {
          this.$emit('error', 'Invalid verification code format');
        }
      }
    },

    // 公共方法：清空验证码
    clear() {
      this.codeValue = '';
    },

    // 公共方法：获取验证码值
    getValue() {
      return this.codeValue;
    },

    // 公共方法：设置验证码值
    setValue(value) {
      if (typeof value === 'string' && /^\d{0,6}$/.test(value)) {
        this.codeValue = value;
      }
    },
  },
};
</script>

<style lang="scss">
.verification-code {
  .form-group {
    margin-bottom: 8px;

    label {
      font: var(--font-fg-14);
      color: var(--black-70);

      span {
        color: var(--alert);
      }
    }
  }

  .input-row {
    display: flex;
    gap: 12px;
    align-items: center;
  }

  .verification-input {
    flex: 1;
    height: 40px;
    padding: 0 12px;
    border: 1px solid var(--black-20);
    border-radius: 6px;
    font-size: 16px;
    font-family: var(--font);
    color: var(--black-90);
    background-color: white;
    transition: border-color 0.2s ease;

    &::placeholder {
      color: var(--black-50);
    }

    &:focus {
      outline: none;
      border-color: var(--dark-blue);
    }

    &:hover {
      border-color: var(--black-50);
    }
  }

  .resend-btn {
    height: 40px;
    padding: 0 16px;
    border: 1px solid var(--blue);
    background-color: var(--blue);
    color: white;
    border-radius: 6px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    white-space: nowrap;

    &:hover:not(:disabled) {
      background-color: var(--dark-blue);
      border-color: var(--dark-blue);
    }

    &:disabled {
      border-color: var(--black-10);
      background-color: var(--black-10);
      color: var(--black-40);
      cursor: not-allowed;
    }
  }

  .error-msg {
    margin: 8px 0 0 0;
    font-size: 12px;
    color: var(--alert);

    &::before {
      content: '*';
      margin-right: 2px;
    }
  }

  &.success .verification-input {
    border-color: var(--green);
  }

  &.error .verification-input {
    border-color: var(--alert);
  }
}
</style>
